import { Button, Card, Form, Input, Space, Typography, message } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { EmptyShipVo } from "@/entity/logistics/emptyShipInfo";
import type { Port } from "@zhihaoscm/entity";
import { searchEmptyShipPorts, type EmptyShipCreateParams } from "./EmptyShipInformationFormPage";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
    DateRangePicker as ZhDateRangePicker,
    Dialog as ZhDialog,
    Grid as ZhGrid,
    InputNumber as ZhInputNumber
} from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { useUserStore } from "@/stores/react/authStore";

export interface EmptyShipInformationEditPageProps {
    emptyShipId?: number | string;
    loadDetail?: (id: number | string) => Promise<EmptyShipVo | undefined>;
    searchPorts?: (keyword: string) => Promise<Port[]>;
    validateEmptyShip?: (params: EmptyShipEditValidationParams) => Promise<boolean>;
    updateEmptyShip?: (id: number | string, params: EmptyShipCreateParams) => Promise<unknown>;
}

/** 编辑校验沿用 Vue 的 id 字段，供后端排除当前正在编辑的空船记录。 */
export type EmptyShipEditValidationParams = EmptyShipCreateParams & { id: number | string };

/** 复用 Vue 编辑页的详情回显接口。 */
export async function loadEmptyShipInformationEditDetail(id: number | string) {
    const response = await shippingRequestHttp.service.get<EmptyShipVo>(`/empty-ship/vo/${id}`);
    return response.data;
}

/** 复用 Vue 编辑页的发布前冲突校验接口。 */
export async function validateEmptyShipEdit(params: EmptyShipEditValidationParams) {
    const response = await shippingRequestHttp.service.post<boolean>("/empty-ship/validator", params);
    return Boolean(response.data);
}

/** 复用 Vue 编辑页的 PUT 更新接口。 */
export async function updateEmptyShipInformation(id: number | string, params: EmptyShipCreateParams) {
    const response = await shippingRequestHttp.service.put(`/empty-ship/${id}`, params);
    return response.data;
}

type EditPort = { id: number | string; type: number | string; shortName: string };
type EditPortSelection = { keyword: string; options: Port[]; selected?: EditPort };

const formatShip = (detail?: EmptyShipVo) => {
    const ship = detail?.emptyShip;
    return ship ? [ship.cnname, ship.name, ship.shipId].filter(Boolean).join(" / ") || DEFAULT_VALUE : DEFAULT_VALUE;
};

const formatCustomer = (detail?: EmptyShipVo) => {
    const carrier = detail?.carrier;
    return carrier ? carrier.institutionName || carrier.realName || DEFAULT_VALUE : DEFAULT_VALUE;
};

const formatDateInput = (value?: string) => value?.slice(0, 10) || "";
const formatDateTime = (value: string, endOfDay = false) => `${value} ${endOfDay ? "23:59:59" : "00:00:00"}`;

/** 使用本地日期生成 date input 可比较的今天边界，保持 Vue disabled-date 规则一致。 */
const getTodayInputValue = () => {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${today.getFullYear()}-${month}-${day}`;
};

const toEditPort = (port: { portId: number; portType: number; portName: string }): EditPort => ({
    id: port.portId,
    type: port.portType,
    shortName: port.portName
});

/** 创建编辑态港口行，保持 Vue 表单至少保留一行可编辑输入。 */
const createEditPortSelection = (): EditPortSelection => ({ keyword: "", options: [] });

/** 物流管理-空船信息编辑，覆盖 Vue 详情回显、锁定字段和更新 payload。 */
export function EmptyShipInformationEditPage({
    emptyShipId,
    loadDetail = loadEmptyShipInformationEditDetail,
    searchPorts = searchEmptyShipPorts,
    validateEmptyShip = validateEmptyShipEdit,
    updateEmptyShip = updateEmptyShipInformation
}: EmptyShipInformationEditPageProps) {
    const routeParams = useParams<{ id: string }>();
    const navigate = useNavigate();
    const banButton = useUserStore(state => state.banButton);
    const resolvedId = emptyShipId ?? routeParams.id ?? "";
    const [detail, setDetail] = useState<EmptyShipVo>();
    const [originPorts, setOriginPorts] = useState<EditPortSelection[]>([createEditPortSelection()]);
    const [destPorts, setDestPorts] = useState<EditPortSelection[]>([createEditPortSelection()]);
    const [emptyStartDate, setEmptyStartDate] = useState("");
    const [emptyEndDate, setEmptyEndDate] = useState("");
    const [remark, setRemark] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmMessage, setConfirmMessage] = useState("");
    const [pendingPayload, setPendingPayload] = useState<EmptyShipCreateParams>();

    useEffect(() => {
        let disposed = false;
        void loadDetail(resolvedId)
            .then(result => {
                if (disposed) return;
                if (!result) {
                    setErrorMessage("空船详情加载失败");
                    return;
                }
                // 编辑态回显 Vue 表单中的船舶、港口、日期和备注字段。
                setDetail(result);
                const originPortValues = result.originPorts || [];
                const destPortValues = result.destPorts || [];
                // 旧 Vue 在无回显数据时保留一个空行，确保编辑页仍可补录港口。
                setOriginPorts(
                    originPortValues.length > 0
                        ? originPortValues.map(port => ({
                              keyword: port.portName,
                              options: [],
                              selected: toEditPort(port)
                          }))
                        : [createEditPortSelection()]
                );
                setDestPorts(
                    destPortValues.length > 0
                        ? destPortValues.map(port => ({
                              keyword: port.portName,
                              options: [],
                              selected: toEditPort(port)
                          }))
                        : [createEditPortSelection()]
                );
                setEmptyStartDate(formatDateInput(result.emptyShip.emptyStartTime));
                setEmptyEndDate(formatDateInput(result.emptyShip.emptyEndTime));
                setRemark(result.emptyShip.remark || "");
                setErrorMessage("");
            })
            .catch(() => {
                if (!disposed) setErrorMessage("空船详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadDetail, resolvedId]);

    const validateForm = () => {
        if (!detail?.emptyShip.shipId) return "空船详情缺少船舶信息";
        if (
            !Number.isInteger(detail.emptyShip.tonCapacity) ||
            detail.emptyShip.tonCapacity <= 0 ||
            detail.emptyShip.tonCapacity > 1000000
        )
            return "请输入1到1000000之间的正整数";
        if (!originPorts.some(item => item.selected)) return "请至少选择1个始发地";
        if (!destPorts.some(item => item.selected)) return "请至少选择1个目的地";
        if (!emptyStartDate || !emptyEndDate) return "请选择预计空船时间范围";
        if (emptyStartDate < getTodayInputValue()) return "开始日期不能早于今天";
        if (emptyEndDate < emptyStartDate) return "结束日期必须晚于开始日期";
        return "";
    };

    const submit = async () => {
        const validationError = validateForm();
        if (validationError) {
            setErrorMessage(validationError);
            return;
        }
        const payload: EmptyShipCreateParams = {
            shipId: detail!.emptyShip.shipId,
            tonCapacity: detail!.emptyShip.tonCapacity,
            originPorts: originPorts
                .filter(item => item.selected)
                .map(item => ({
                    portId: String(item.selected!.id),
                    portType: String(item.selected!.type),
                    portName: item.selected!.shortName
                })),
            destPorts: destPorts
                .filter(item => item.selected)
                .map(item => ({
                    portId: String(item.selected!.id),
                    portType: String(item.selected!.type),
                    portName: item.selected!.shortName
                })),
            emptyStartTime: formatDateTime(emptyStartDate),
            emptyEndTime: formatDateTime(emptyEndDate, true),
            remark
        };
        setLoading(true);
        try {
            const validationPayload: EmptyShipEditValidationParams = { ...payload, id: resolvedId };
            const valid = await validateEmptyShip(validationPayload);
            setPendingPayload(payload);
            setConfirmMessage(
                valid
                    ? "确认发布吗？"
                    : `船舶 ${formatShip(detail)} 在您指定的空船时间和航线上，系统内已有高度重合的空船信息，继续提交可能导致信息冲突，确认发布吗？`
            );
            setConfirmOpen(true);
        } catch {
            setErrorMessage("空船信息校验失败");
        } finally {
            setLoading(false);
        }
    };

    /** 确认后沿用 Vue 编辑页的 PUT payload 更新空船信息。 */
    const confirmSubmit = async () => {
        if (!pendingPayload) return;
        setLoading(true);
        try {
            await updateEmptyShip(resolvedId, pendingPayload);
            message.success("发布成功");
            setConfirmOpen(false);
            navigate(-1);
        } catch {
            setErrorMessage("空船信息更新失败");
        } finally {
            setLoading(false);
        }
    };

    /** 保持旧 Vue 日期范围选择器禁止今天以前日期的规则。 */
    const disabledPastDates = (date: Date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date < today;
    };

    const handlePortSearch = async (keyword: string, type: "origin" | "dest", index: number) => {
        const updateItems = type === "origin" ? setOriginPorts : setDestPorts;
        updateItems(items =>
            items.map((item, itemIndex) =>
                itemIndex === index ? { ...item, keyword, options: [], selected: undefined } : item
            )
        );
        if (!keyword) return;
        try {
            const results = await searchPorts(keyword);
            updateItems(items =>
                items.map((item, itemIndex) => (itemIndex === index ? { ...item, options: results } : item))
            );
        } catch {
            setErrorMessage("港口搜索失败");
        }
    };

    const selectPort = (type: "origin" | "dest", index: number, port: Port) => {
        const updateItems = type === "origin" ? setOriginPorts : setDestPorts;
        updateItems(items =>
            items.map((item, itemIndex) =>
                itemIndex === index
                    ? {
                          ...item,
                          keyword: port.shortName,
                          options: [],
                          selected: { id: port.id, type: port.type, shortName: port.shortName }
                      }
                    : item
            )
        );
    };

    const addPortItem = (type: "origin" | "dest") => {
        const updateItems = type === "origin" ? setOriginPorts : setDestPorts;
        const currentItems = type === "origin" ? originPorts : destPorts;
        if (currentItems.length >= 10) {
            // 对齐旧 Vue 的边界反馈：达到最多十项时提示用户，而不是静默忽略点击。
            message.warning(`最多只能选择10个${type === "origin" ? "始发地" : "目的地"}！`);
            return;
        }
        updateItems(items => (items.length >= 10 ? items : [...items, createEditPortSelection()]));
    };

    const removePortItem = (type: "origin" | "dest", index: number) => {
        const updateItems = type === "origin" ? setOriginPorts : setDestPorts;
        updateItems(items => (items.length > 1 ? items.filter((_, itemIndex) => itemIndex !== index) : items));
    };

    const ship = detail?.emptyShip;
    return (
        <section aria-label="empty-ship-information-edit-page" className="flex min-h-full flex-col">
            <Card>
                <Typography.Title level={4}>编辑空船信息</Typography.Title>
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                <Form layout="vertical">
                    <Typography.Title level={5}>船舶信息</Typography.Title>
                    <ZhGrid cols={4} gap={[30, 0]}>
                        <Form.Item label="船舶名称" required>
                            <Input aria-label="船舶名称" disabled value={formatShip(detail)} />
                        </Form.Item>
                        <Form.Item label="承运商" required>
                            <Input aria-label="承运商" disabled value={formatCustomer(detail)} />
                        </Form.Item>
                        <Form.Item label="联系人" required>
                            <Input aria-label="联系人" disabled value={ship?.captain || ""} />
                        </Form.Item>
                        <Form.Item label="联系方式" required>
                            <Input aria-label="联系方式" disabled value={ship?.mobile || ""} />
                        </Form.Item>
                        <Form.Item label="实装吨位" required>
                            <ZhInputNumber
                                aria-label="实装吨位"
                                disabled
                                value={ship?.tonCapacity || ""}
                                min={1}
                                max={1000000}
                                precision={0}
                            />
                        </Form.Item>
                    </ZhGrid>
                    <Typography.Title level={5}>空船信息</Typography.Title>
                    <ZhGrid cols={1} gap={[30, 0]}>
                        <Form.Item label="空船始发地" required>
                            {originPorts.map((item, index) => (
                                <Space key={`origin-${index}`} wrap>
                                    <Input
                                        aria-label={index === 0 ? "始发地搜索" : `始发地搜索${index + 1}`}
                                        placeholder="请输入码头名称或城市选择"
                                        value={item.keyword}
                                        onChange={event => void handlePortSearch(event.target.value, "origin", index)}
                                    />
                                    {item.selected ? (
                                        <Typography.Text>已选择：{item.selected.shortName}</Typography.Text>
                                    ) : (
                                        item.options.map(port => (
                                            <Button
                                                key={port.id}
                                                type="link"
                                                onClick={() => selectPort("origin", index, port)}
                                            >
                                                {port.shortName}
                                            </Button>
                                        ))
                                    )}
                                    <Button aria-label="添加始发地" onClick={() => addPortItem("origin")}>
                                        +
                                    </Button>
                                    {index > 0 ? (
                                        <Button
                                            aria-label={`删除始发地${index + 1}`}
                                            onClick={() => removePortItem("origin", index)}
                                        >
                                            -
                                        </Button>
                                    ) : null}
                                </Space>
                            ))}
                        </Form.Item>
                        <Form.Item label="目的地" required>
                            {destPorts.map((item, index) => (
                                <Space key={`dest-${index}`} wrap>
                                    <Input
                                        aria-label={index === 0 ? "目的地搜索" : `目的地搜索${index + 1}`}
                                        placeholder="请输入码头名称或城市选择"
                                        value={item.keyword}
                                        onChange={event => void handlePortSearch(event.target.value, "dest", index)}
                                    />
                                    {item.selected ? (
                                        <Typography.Text>已选择：{item.selected.shortName}</Typography.Text>
                                    ) : (
                                        item.options.map(port => (
                                            <Button
                                                key={port.id}
                                                type="link"
                                                onClick={() => selectPort("dest", index, port)}
                                            >
                                                {port.shortName}
                                            </Button>
                                        ))
                                    )}
                                    <Button aria-label="添加目的地" onClick={() => addPortItem("dest")}>
                                        +
                                    </Button>
                                    {index > 0 ? (
                                        <Button
                                            aria-label={`删除目的地${index + 1}`}
                                            onClick={() => removePortItem("dest", index)}
                                        >
                                            -
                                        </Button>
                                    ) : null}
                                </Space>
                            ))}
                        </Form.Item>
                    </ZhGrid>
                    <ZhGrid cols={3} gap={[30, 0]}>
                        <Form.Item label="预计空船时间" required>
                            <ZhDateRangePicker
                                aria-label="预计空船时间"
                                disabledDate={disabledPastDates}
                                endPlaceholder="请选择结束日期"
                                format="YYYY-MM-DD"
                                rangeSeparator="-"
                                startPlaceholder="请选择开始日期"
                                type="daterange"
                                value={[emptyStartDate || null, emptyEndDate || null]}
                                valueFormat="YYYY-MM-DD"
                                onChange={(_, formattedValue) => {
                                    setEmptyStartDate(formattedValue?.[0] || "");
                                    setEmptyEndDate(formattedValue?.[1] || "");
                                }}
                            />
                            {/* 保留旧 Vue 字段名的无障碍入口，兼容既有表单自动化并同步日期草稿。 */}
                            <input
                                aria-label="空船开始日期"
                                className="sr-only"
                                type="date"
                                value={emptyStartDate}
                                onChange={event => setEmptyStartDate(event.target.value)}
                            />
                            <input
                                aria-label="空船结束日期"
                                className="sr-only"
                                type="date"
                                value={emptyEndDate}
                                onChange={event => setEmptyEndDate(event.target.value)}
                            />
                        </Form.Item>
                    </ZhGrid>
                    <ZhGrid cols={1} gap={[30, 0]}>
                        <Form.Item label="备注">
                            <Input.TextArea
                                aria-label="备注"
                                maxLength={200}
                                placeholder="请输入"
                                value={remark}
                                onChange={event => setRemark(event.target.value)}
                            />
                        </Form.Item>
                    </ZhGrid>
                    <Space>
                        <Button
                            aria-label="提交"
                            disabled={banButton}
                            loading={loading}
                            type="primary"
                            onClick={() => void submit()}
                        >
                            提交
                        </Button>
                        <Button onClick={() => navigate(-1)}>返回</Button>
                    </Space>
                </Form>
            </Card>
            <ZhDialog
                open={confirmOpen}
                title="提示"
                closeOnClickModal={false}
                modalRender={node => <div role="dialog">{node}</div>}
                footer={
                    <Space>
                        <Button onClick={() => setConfirmOpen(false)}>取消</Button>
                        <Button loading={loading} type="primary" onClick={() => void confirmSubmit()}>
                            确认
                        </Button>
                    </Space>
                }
                onCancel={() => setConfirmOpen(false)}
            >
                {confirmMessage}
            </ZhDialog>
        </section>
    );
}
